import {
  addDays,
  differenceInCalendarDays,
  endOfMonth,
  format,
  isSameDay,
  isSameYear,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
} from "date-fns";
import { ptBR } from "date-fns/locale";

/** Intervalo de datas de calendario, inclusivo nas duas pontas. */
export type Intervalo = { readonly inicio: Date; readonly fim: Date };

export type IdDoPeriodoPronto =
  | "hoje"
  | "ontem"
  | "ultimos-7-dias"
  | "ultimos-30-dias"
  | "este-mes"
  | "mes-passado"
  | "ultimos-90-dias"
  | "este-ano";

type PeriodoPronto = {
  readonly id: IdDoPeriodoPronto;
  readonly rotulo: string;
  readonly calcular: (hoje: Date) => Intervalo;
};

/**
 * Periodos prontos do seletor. Funcoes de `hoje`, e nao do relogio: o teste
 * fixa a data e o resultado nao muda na virada do mes.
 */
export const PERIODOS_PRONTOS: readonly PeriodoPronto[] = [
  { id: "hoje", rotulo: "Hoje", calcular: (hoje) => ({ inicio: hoje, fim: hoje }) },
  { id: "ontem", rotulo: "Ontem", calcular: (hoje) => ({ inicio: subDays(hoje, 1), fim: subDays(hoje, 1) }) },
  { id: "ultimos-7-dias", rotulo: "Últimos 7 dias", calcular: (hoje) => ({ inicio: subDays(hoje, 6), fim: hoje }) },
  { id: "ultimos-30-dias", rotulo: "Últimos 30 dias", calcular: (hoje) => ({ inicio: subDays(hoje, 29), fim: hoje }) },
  { id: "este-mes", rotulo: "Este mês", calcular: (hoje) => ({ inicio: startOfMonth(hoje), fim: hoje }) },
  {
    id: "mes-passado",
    rotulo: "Mês passado",
    calcular: (hoje) => {
      const mes = subMonths(hoje, 1);
      return { inicio: startOfMonth(mes), fim: endOfMonth(mes) };
    },
  },
  { id: "ultimos-90-dias", rotulo: "Últimos 90 dias", calcular: (hoje) => ({ inicio: subDays(hoje, 89), fim: hoje }) },
  { id: "este-ano", rotulo: "Este ano", calcular: (hoje) => ({ inicio: startOfYear(hoje), fim: hoje }) },
];

export const PERIODO_PADRAO: IdDoPeriodoPronto = "este-mes";

export function periodoPronto(id: IdDoPeriodoPronto, hoje: Date): Intervalo {
  const pronto = PERIODOS_PRONTOS.find((item) => item.id === id) ?? PERIODOS_PRONTOS[3];
  return (pronto as PeriodoPronto).calcular(hoje);
}

/** Qual periodo pronto bate com o intervalo (para marcar no seletor). */
export function identificarPeriodoPronto(intervalo: Intervalo, hoje: Date): PeriodoPronto | null {
  return (
    PERIODOS_PRONTOS.find((pronto) => {
      const candidato = pronto.calcular(hoje);
      return isSameDay(candidato.inicio, intervalo.inicio) && isSameDay(candidato.fim, intervalo.fim);
    }) ?? null
  );
}

/** Periodo de mesmo tamanho imediatamente antes: base de comparacao. */
export function periodoAnterior(intervalo: Intervalo): Intervalo {
  const dias = diasNoIntervalo(intervalo);
  return { inicio: subDays(intervalo.inicio, dias), fim: subDays(intervalo.inicio, 1) };
}

export function diasNoIntervalo(intervalo: Intervalo): number {
  return differenceInCalendarDays(intervalo.fim, intervalo.inicio) + 1;
}

/** Cada dia do intervalo, em ordem. */
export function diasDe(intervalo: Intervalo): Date[] {
  return Array.from({ length: diasNoIntervalo(intervalo) }, (_, indice) => addDays(intervalo.inicio, indice));
}

/** "1 – 30 set 2026", "28 ago – 3 set 2026" ou "5 set 2026". */
export function descreverIntervalo(intervalo: Intervalo): string {
  const { inicio, fim } = intervalo;
  if (isSameDay(inicio, fim)) return format(inicio, "d MMM yyyy", { locale: ptBR });
  const mesmoMes = inicio.getMonth() === fim.getMonth() && isSameYear(inicio, fim);
  const formatoDoInicio = mesmoMes ? "d" : isSameYear(inicio, fim) ? "d MMM" : "d MMM yyyy";
  return `${format(inicio, formatoDoInicio, { locale: ptBR })} – ${format(fim, "d MMM yyyy", { locale: ptBR })}`;
}

/** Data de calendario no formato do contrato (aaaa-mm-dd), sem fuso. */
export function paraDataIso(data: Date): string {
  return format(data, "yyyy-MM-dd");
}
